import SiteLayout from "@/components/SiteLayout";
import { FileText, Layers, MapPin, Gauge, AlertCircle, BookOpen, Bot, ShieldCheck } from "lucide-react";

const Block = ({ icon: Icon, title, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7">
    <div className="flex items-center gap-3 mb-3">
      <span className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"><Icon className="w-5 h-5 text-slate-700" strokeWidth={1.75} /></span>
      <h2 className="font-heading text-xl font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="text-slate-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Metodologia() {
  return (
    <SiteLayout
      seo={{ title: "Metodologia", description: "La metodologia di OKKIO: cosa estraiamo dal documento, come distinguiamo fatti, inferenze e benchmark, la gerarchia geografica dei riferimenti, i livelli di confidenza e i limiti dell’analisi.", path: "/metodologia" }}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Metodologia", to: "/metodologia" }]}
      narrow>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">Metodologia</h1>
      <p className="mt-5 text-lg text-slate-600 leading-relaxed">
        Trasparenza prima di tutto. Ecco come analizziamo un preventivo e, soprattutto, cosa facciamo quando i dati non bastano.
      </p>

      <div className="mt-10 space-y-4">
        <Block icon={FileText} title="Cosa estraiamo dal documento">
          <p>Ogni preventivo viene trasformato in dati strutturati: categoria e sottocategoria della lavorazione,
            descrizione originale, quantità, unità di misura, prezzo unitario e totale, indicazione di materiale incluso,
            marca/modello quando disponibile e trattamento dell’IVA. Non conserviamo il preventivo solo come immagine:
            lo scomponiamo in voci confrontabili.</p>
        </Block>

        <Block icon={Layers} title="Fatti, inferenze e benchmark">
          <p>Distinguiamo sempre tre livelli. Un <b>fatto</b> è ciò che è scritto nel documento. Un’<b>inferenza</b> è una
            deduzione prudente (ad esempio: una lavorazione che di norma accompagna un’altra e potrebbe mancare). Un
            <b> benchmark</b> è un confronto con riferimenti di mercato esterni. Ogni segnalazione indica a quale livello appartiene.</p>
        </Block>

        <Block icon={MapPin} title="Gerarchia geografica dei benchmark">
          <p>I riferimenti di prezzo hanno senso solo se contestualizzati. La nostra architettura è pensata per costruire
            benchmark su più livelli: <b>Comune → Provincia → Regione → Macro-area → Italia</b>. Quando manca un riferimento
            locale abbastanza solido, saliamo di livello, indicando sempre quale ambito è stato usato.</p>
        </Block>

        <Block icon={Gauge} title="Livello di confidenza">
          <p>A ogni voce associamo un livello di qualità del dato e un confidence score: dipende da quanto il preventivo
            è dettagliato e da quanto è affidabile il riferimento disponibile. Un confronto su una voce “a corpo” senza
            quantità avrà una confidenza più bassa di una voce con prezzo unitario e quantità chiare.</p>
        </Block>

        <Block icon={AlertCircle} title="Quando non ci sono dati sufficienti">
          <p>Se non esiste un riferimento affidabile per una voce, <b>non inventiamo un prezzo</b>: la classifichiamo come
            “dati insufficienti per una valutazione affidabile”. Preferiamo dichiarare un limite piuttosto che fornire una
            stima non verificabile.</p>
        </Block>

        <Block icon={BookOpen} title="Fonti utilizzabili">
          <p>Utilizziamo, dove pertinenti e verificabili, riferimenti di mercato e prezzari regionali pubblici, oltre ai
            dati aggregati che raccogliamo nel tempo dai preventivi analizzati. Indichiamo la natura della fonte e non
            presentiamo come “ufficiale” un dato che non lo è.</p>
        </Block>

        <Block icon={Bot} title="Limiti dell’analisi AI">
          <p>L’analisi è prodotta con l’ausilio dell’intelligenza artificiale e può contenere imprecisioni, soprattutto con
            documenti poco leggibili o molto sintetici. OKKIO fornisce una seconda opinione a scopo informativo: non
            sostituisce il sopralluogo né la consulenza di un professionista abilitato.</p>
        </Block>

        <Block icon={ShieldCheck} title="Indipendenza commerciale">
          <p>Non siamo un’impresa edile, non vendiamo ristrutturazioni e non riceviamo compensi dalle imprese analizzate.
            Non modifichiamo l’esito dell’analisi per favorire nessuno. Il nostro cliente sei tu, non l’impresa.</p>
        </Block>
      </div>
    </SiteLayout>
  );
}
